import { MetricPillInput } from '../../../../components/metrics/MetricPillInput';
import { RatingRow } from '../../../../components/metrics/RatingRow';
import { COMMON_VALUES, METRIC_RANGES, METRIC_UNITS } from '../../../../constants/metrics';
import type { PitchConfig } from '../../../../constants/pitchConfig';
import type { ProspectPitcherMetricsResponse, MetricEntryResponse } from '../../../../types/api';
import './PitcherMetricsSection.css';

interface PitcherMetricsSectionProps {
  metrics: Partial<ProspectPitcherMetricsResponse>;
  onMetricChange: (field: string, value: number | null) => void;
  pitchConfigs: PitchConfig[];
  /** Optional: prospect ID for metric history tracking */
  prospectId?: string;
  /** Optional: all metric entries for the prospect (from prospect response) */
  metricEntries?: MetricEntryResponse[];
  /** Optional: Current user ID to show "You" for entries recorded by the logged-in user */
  currentUserId?: string;
}

export function PitcherMetricsSection({
  metrics,
  onMetricChange,
  pitchConfigs,
  prospectId,
  metricEntries = [],
  currentUserId,
}: PitcherMetricsSectionProps) {
  return (
    <div className="pitcher-metrics">
      {/* Velocity Section */}
      <div className="pitcher-metrics__section">
        <h4 className="pitcher-metrics__section-title">Pitch Velocities</h4>

        {pitchConfigs.map((pitch) => {
          // Read into a const so the truthiness check below narrows inside the onChange
          // closure too. Baseball pitches have no spin rate and render exactly as before.
          const spinRateField = pitch.spinRateField;

          return (
            <div
              key={pitch.key}
              className={`pitcher-metrics__pitch-group${
                spinRateField ? ' pitcher-metrics__pitch-group--with-spin' : ''
              }`}
            >
              <MetricPillInput
                label={pitch.displayName}
                value={metrics[pitch.velocityField] ?? null}
                commonValues={pitch.commonValues}
                unit={METRIC_UNITS.velocity}
                onChange={(value) => onMetricChange(pitch.velocityField, value)}
                min={METRIC_RANGES.velocity.min}
                max={METRIC_RANGES.velocity.max}
                decimalPlaces={1}
                prospectId={prospectId}
                metricName={pitch.velocityField}
                metricCategory="pitcher"
                metricEntries={metricEntries}
                currentUserId={currentUserId}
              />

              {/* Spin rate is decimal on the wire but whole rpm in practice, hence 0dp. */}
              {spinRateField && (
                <MetricPillInput
                  label={`${pitch.displayName} Spin Rate`}
                  value={metrics[spinRateField] ?? null}
                  commonValues={COMMON_VALUES.spinRate}
                  unit={METRIC_UNITS.spinRate}
                  onChange={(value) => onMetricChange(spinRateField, value)}
                  min={METRIC_RANGES.spinRate.min}
                  max={METRIC_RANGES.spinRate.max}
                  // A spin rate outside the range is refused with a message rather than
                  // clamped, so a mistyped 4500 is never saved as 2800.
                  rejectOutOfRange
                  decimalPlaces={0}
                  prospectId={prospectId}
                  metricName={spinRateField}
                  metricCategory="pitcher"
                  metricEntries={metricEntries}
                  currentUserId={currentUserId}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Ratings Section */}
      <div className="pitcher-metrics__section">
        <h4 className="pitcher-metrics__section-title">80/20 Scale Grades</h4>

        <div className="pitcher-metrics__rating-header">
          <span className="pitcher-metrics__rating-header-label"></span>
          <div className="pitcher-metrics__rating-header-columns">
            <span className="pitcher-metrics__rating-header-column">Present</span>
            <span className="pitcher-metrics__rating-header-column">Future</span>
          </div>
        </div>

        {pitchConfigs.map((pitch) => (
          <RatingRow
            key={pitch.key}
            label={`${pitch.displayName}:`}
            // Every pitch names its grade fields for screen readers. Screen-reader-only and
            // invisible on screen, so it is not part of what the baseball experience holds
            // constant — and matches what mobile now does.
            accessibilityLabelPrefix={pitch.displayName}
            testIdPrefix={pitch.key}
            presentValue={metrics[pitch.presentField] ?? null}
            futureValue={metrics[pitch.futureField] ?? null}
            onPresentChange={(value) => onMetricChange(pitch.presentField, value)}
            onFutureChange={(value) => onMetricChange(pitch.futureField, value)}
          />
        ))}
      </div>
    </div>
  );
}
